import { useAuth } from "@lib/api-hooks";
import { useCallback, useState } from "react";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import { Button } from "@tracify/ui/button";
import { Input } from "@tracify/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@tracify/ui/dialog";
import { useCreateFacebookDataset } from "@lib/api-hooks/useFacebookPixel";

type Props = {
  onClose: (success?: boolean) => void;
  open: boolean;
};

const CreateFacebookPixelModal = ({ onClose, open }: Props) => {
  const { data: auth } = useAuth();
  const { selectedOrganisationData } = useSelectedStores();
  const createFacebookDatasetMutation = useCreateFacebookDataset();

  const [storeErrorMessage, setStoreErrorMessage] = useState("");
  const [facebookDatasetCredentials, setFacebookDatasetCredentials] = useState({
    dataset_id: "",
    capi_token: "",
  });

  const onConnectPixel = useCallback(async () => {
    setStoreErrorMessage("");

    if (!selectedOrganisationData) {
      setStoreErrorMessage(
        "You must select an organisation to connect a Facebook dataset."
      );
    } else {
      try {
        if (!facebookDatasetCredentials.dataset_id) {
          setStoreErrorMessage("Please provide a Dataset ID");
          return;
        }

        if (!facebookDatasetCredentials.capi_token) {
          setStoreErrorMessage("Please provide a CAPI API token");
          return;
        }

        await createFacebookDatasetMutation.mutateAsync({
          organisationId: selectedOrganisationData.id,
          dataset_id: facebookDatasetCredentials.dataset_id,
          capi_token: facebookDatasetCredentials.capi_token,
        });
        setFacebookDatasetCredentials({
          dataset_id: "",
          capi_token: "",
        });
        onClose(true);
      } catch (error: any) {
        setStoreErrorMessage(error.message);
        return;
      }
    }
  }, [
    createFacebookDatasetMutation,
    selectedOrganisationData,
    facebookDatasetCredentials,
    onClose,
  ]);

  const renderFacebookPixelForm = () => (
    <div className="space-y-4 mt-4">
      <div>
        <label htmlFor="dataset_id" className="block text-sm font-medium mb-1">
          Meta Dataset ID
        </label>
        <Input
          id="dataset_id"
          type="text"
          value={facebookDatasetCredentials.dataset_id}
          onChange={(e) =>
            setFacebookDatasetCredentials((prev) => ({
              ...prev,
              dataset_id: e.target.value,
            }))
          }
          placeholder="Enter your Meta Dataset ID"
        />
        <p className="text-xs text-muted-foreground mt-1">
          <a
            href="https://knowledge.tracify.ai/en/articles/647895-how-to-create-a-new-meta-dataset-pixel"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            How to Create a New Meta Dataset
          </a>
        </p>
      </div>
      <div>
        <label htmlFor="capi_token" className="block text-sm font-medium mb-1">
          Meta CAPI API token
        </label>
        <Input
          id="capi_token"
          type="password"
          value={facebookDatasetCredentials.capi_token}
          onChange={(e) =>
            setFacebookDatasetCredentials((prev) => ({
              ...prev,
              capi_token: e.target.value,
            }))
          }
          placeholder="Enter your Meta CAPI API token"
        />
      </div>
      {storeErrorMessage && (
        <p className="mt-4 font-medium text-sm text-red-500">
          {storeErrorMessage}
        </p>
      )}
    </div>
  );

  const noteHeader = (
    <div className="space-y-4">
      <p className="font-semibold">Prerequisite:</p>
      <p>
        You need to have a Meta Business Manager account with access to the Meta
        Dataset you want to connect.
      </p>
    </div>
  );

  const noteContent = (
    <div className="space-y-4">
      <ul className="list-none space-y-1">
        <li className="flex items-start gap-1">
          <div className="w-1 h-5 flex justify-center items-center mt-0.5">
            <div className="flex-1 h-1 bg-foreground rounded-full"></div>
          </div>
          <div className="flex-1">
            Go to your Meta Business Manager and navigate to Events Manager.
          </div>
        </li>
        <li className="flex items-start gap-1">
          <div className="w-1 h-5 flex justify-center items-center mt-0.5">
            <div className="flex-1 h-1 bg-foreground rounded-full"></div>
          </div>
          <div className="flex-1">
            Find your Meta Dataset ID in the Events Manager dashboard.
          </div>
        </li>
        <li className="flex items-start gap-1">
          <div className="w-1 h-5 flex justify-center items-center mt-0.5">
            <div className="flex-1 h-1 bg-foreground rounded-full"></div>
          </div>
          <div className="flex-1">
            Enter the Dataset ID below to connect your dataset to Tracify.
          </div>
        </li>
      </ul>
      <p>
        Note: Your Meta account must have access to the dataset you want to
        connect.
      </p>
    </div>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Enable conversion postback</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col mt-4">
            <div className="bg-primary-300/15 border border-primary-300/70 rounded-md p-4 text-sm space-y-4">
              {noteHeader}
              {noteContent}
              <p>
                <span className="font-semibold">
                  This connection allows us to track conversions and optimize
                  your Meta ad campaigns.{" "}
                </span>
                <a
                  href="https://knowledge.tracify.ai/en/articles/249883-why-does-tracify-need-permissions-to-my-ad-account"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold hover:underline"
                >
                  Learn more
                </a>
              </p>
            </div>
            {renderFacebookPixelForm()}

            <div className="flex justify-end mt-4">
              <Button
                variant="primary"
                disabled={
                  !!auth?.payload?.ema &&
                  auth?.payload?.ema?.endsWith("@tracify.ai")
                    ? false
                    : false
                }
                loading={createFacebookDatasetMutation.isPending}
                onClick={onConnectPixel}
              >
                Enable conversion postback
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreateFacebookPixelModal;
